import { useSupportChatStore } from '../../supportChatStore.ts';
import { getSierraVariables } from './getSierraVariables.ts';
import { getSierraSecretsConfig } from './getSierraSecretsConfig.ts';
import {
    trackChatOpened,
    trackChatClosed,
    trackConversationStarted,
    trackConversationEnded,
    trackUserMessageSent,
    trackAgentMessageSent,
    trackAgentAttachmentSent,
    trackHumanTransferInitiated,
    trackHumanAgentJoin,
} from '../tracking.ts';
import { SIERRA_CONFIG, SIERRA_LOCALE_MAP } from '../constants.ts';

import { type IntlShape } from 'dibs-react-intl';
import { type SierraConfig } from '../types/SierraAPIConfig.ts';
import { type SupportChatTouchpoint, type PreChatFields } from '../../types.ts';

type GetSierraConfigOptions = {
    touchpoint: SupportChatTouchpoint;
    preChatFields: PreChatFields;
    locale: IntlShape['locale'];
    userToken: string | null | undefined;
    sellerToken: string | null | undefined;
    sfChatToken: string | null | undefined;
    onSfChatTokenRefresh: () => Promise<string | null>;
};

function getSierraConfig({
    touchpoint,
    preChatFields,
    locale,
    userToken,
    sellerToken,
    sfChatToken,
    onSfChatTokenRefresh,
}: GetSierraConfigOptions): SierraConfig {
    const getStore = useSupportChatStore.getState;
    const {
        setIsInitialized,
        setHasActiveChat,
        setIsButtonVisible,
        setIsWindowVisible,
        setHasUnreadMessage,
    } = getStore();

    const placement = preChatFields?.pageType || 'support chat';
    const topic = preChatFields?.topic;

    return {
        ...SIERRA_CONFIG,
        locale: SIERRA_LOCALE_MAP[locale],
        variables: getSierraVariables({
            touchpoint,
            preChatFields,
        }),
        ...getSierraSecretsConfig({
            userToken,
            sellerToken,
            sfChatToken,
            onSfChatTokenRefresh,
        }),
        onAgentMessageStart: () => {
            if (!getStore().isWindowVisible) {
                setHasUnreadMessage(true);
            }
            trackAgentMessageSent({ type: 'start', placement, topic });
        },
        onAgentMessageEnd: () => {
            trackAgentMessageSent({ type: 'end', placement, topic });
        },
        onAgentAttachment: () => {
            trackAgentAttachmentSent({ placement, topic });
        },
        onUserMessage: () => {
            trackUserMessageSent({ placement, topic });
        },
        onExternalAgentJoin: () => {
            trackHumanAgentJoin({ placement, topic });
        },
        onTransfer: () => {
            trackHumanTransferInitiated({ placement, topic });
        },
        onOpen: () => {
            setIsButtonVisible(false);
            setIsWindowVisible(true);
            trackChatOpened({ placement, topic });
        },
        onClose: () => {
            setIsWindowVisible(false);
            if (getStore().hasActiveChat) {
                setIsButtonVisible(true);
            } else {
                window.sierra?.cleanup?.();
                setIsInitialized(false);
                setIsButtonVisible(getStore().showChatButtonOnLoad);
            }
            trackChatClosed({ placement, topic });
        },
        onConversationEnd: () => {
            setHasActiveChat(false);
            trackConversationEnded({ placement, topic });
        },
        onConversationStart: () => {
            setHasActiveChat(true);
            trackConversationStarted({ placement, topic });
        },
        onLoad: () => {
            setIsInitialized(true);
        },
    };
}

export { getSierraConfig };
